import axios from 'axios';
import { refreshMsalToken } from '../context/MicrosoftAuthContext';

// Create axios instance with default config
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080', // Your backend URL
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

// Add a request interceptor
api.interceptors.request.use(
    (config) => {
        console.log(`API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);

        // Get the JWT token from localStorage (prioritize JWT token)
        let token = localStorage.getItem('gift5tjwtToken');

        if (token) {
            console.log('Using JWT token from localStorage');
        } else {
            // Fall back to the Microsoft token if no JWT token found
            token = localStorage.getItem('msalToken');
            if (token) {
                console.log('Using Microsoft token from localStorage as fallback');
            } else {
                // Legacy token support
                token = localStorage.getItem('token');
                if (token) {
                    console.log('Using legacy token from localStorage');
                } else {
                    console.log('No token found in storage');
                }
            }
        }

        // If token exists, add it to headers
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('Authorization header set for request');
        } else {
            console.log('No authorization header set for request');
        }

        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add a response interceptor
api.interceptors.response.use(
    (response) => {
        console.log(`API Response: ${response.status} ${response.config.method.toUpperCase()} ${response.config.url}`);
        return response;
    },
    async (error) => {
        // Original request config
        const originalRequest = error.config;

        // Handle API errors
        if (error.response) {
            console.error(`API Error: ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
            console.error('Error response data:', error.response.data);

            // Handle 401 Unauthorized errors
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                console.log('Unauthorized (401) error detected, attempting token refresh');

                try {
                    // Attempt to refresh the token
                    // We need to dispatch this event to be handled by the auth context
                    const refreshEvent = new CustomEvent('token:refresh:required');
                    window.dispatchEvent(refreshEvent);

                    // Wait for a response from the auth context (handled via an event)
                    const refreshResult = await new Promise((resolve, reject) => {
                        // Set up a listener for the refresh completion
                        const handleRefreshComplete = (event) => {
                            window.removeEventListener('token:refresh:complete', handleRefreshComplete);
                            if (event.detail.success) {
                                resolve(event.detail.token);
                            } else {
                                reject(new Error('Token refresh failed'));
                            }
                        };

                        // Listen for the refresh completion event
                        window.addEventListener('token:refresh:complete', handleRefreshComplete);

                        // Set a timeout for token refresh
                        setTimeout(() => {
                            window.removeEventListener('token:refresh:complete', handleRefreshComplete);
                            reject(new Error('Token refresh timeout'));
                        }, 10000);
                    });

                    // If we get here, the token was refreshed successfully
                    console.log('Token refreshed successfully, retrying request');

                    // Update the authorization header with the new token
                    originalRequest.headers.Authorization = `Bearer ${refreshResult}`;

                    // Retry the original request with the new token
                    return api(originalRequest);
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);

                    // Clear all possible tokens
                    localStorage.removeItem('token');
                    localStorage.removeItem('msalToken');
                    localStorage.removeItem('gift5tjwtToken');

                    // Redirect to login
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
            }
        } else if (error.request) {
            console.error('API request made but no response received:', error.request);
        } else {
            console.error('API request error:', error.message);
        }
        return Promise.reject(error);
    }
);

// Apply default authorization header if token exists on load
const token = localStorage.getItem('gift5tjwtToken') || localStorage.getItem('msalToken') || localStorage.getItem('token');
if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('Default Authorization header set on initialization with token');
}

export default api; 